"use client";

import React, { useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { InputWithPicker, TextareaWithPicker } from '@/components/forms/FieldWithPicker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Plus, Trash2, Image as ImageIcon, Video, FileText, Phone, Copy, ExternalLink, MessageSquare, PhoneCall } from 'lucide-react';
import { ButtonFormData } from '@/types/messages';
import {
  containsDisallowedCharOutsideSpintax,
  stripDisallowedOutsideSpintax,
} from '@/utils/spintax';

const BUTTON_DISALLOWED = '|*';
const stripButtonDisallowed = (value: string) =>
  stripDisallowedOutsideSpintax(value, BUTTON_DISALLOWED);
const hasInvalidButtonChars = (value: string) =>
  containsDisallowedCharOutsideSpintax(value, BUTTON_DISALLOWED);
const invalidButtonCharsMessage =
  'Cannot include * or | outside of spintax blocks like {Hi|Hello}';

const buttonFormSchema = z.object({
  title: z.string().min(1, 'Title is required').refine((v) => !hasInvalidButtonChars(v), invalidButtonCharsMessage),
  subTitle: z.string().optional().refine((v) => !v || !hasInvalidButtonChars(v), invalidButtonCharsMessage),
  mediaType: z.enum(['none', 'image', 'video', 'document']),
  mediaUrl: z.string().optional().refine((v) => !v || !hasInvalidButtonChars(v), invalidButtonCharsMessage),
  mediaFilename: z.string().optional().refine((v) => !v || !hasInvalidButtonChars(v), invalidButtonCharsMessage),
  buttons: z.array(z.object({
    type: z.enum(['cta_copy', 'cta_call', 'cta_url', 'quick_reply', 'voice_call']),
    text: z.string().min(1, 'Button text is required').refine((v) => !hasInvalidButtonChars(v), invalidButtonCharsMessage),
    id: z.string().optional().refine((v) => !v || !hasInvalidButtonChars(v), invalidButtonCharsMessage),
    url: z.string().optional().refine((v) => !v || !hasInvalidButtonChars(v), invalidButtonCharsMessage),
    phoneNumber: z.string().optional().refine((v) => !v || !hasInvalidButtonChars(v), invalidButtonCharsMessage),
    copyCode: z.string().optional().refine((v) => !v || !hasInvalidButtonChars(v), invalidButtonCharsMessage),
  })).min(1, 'At least one button is required').max(10, 'Maximum 10 buttons allowed'),
}).refine((data) => {
  if (data.mediaType !== 'none' && !data.mediaUrl) {
    return false;
  }
  return true;
}, {
  message: "Media URL is required when media type is selected",
  path: ["mediaUrl"],
}).refine((data) => {
  return data.buttons.every(button => {
    if (button.type === 'cta_url') {
      return button.url && button.url.trim() !== '';
    }
    if (button.type === 'cta_call') {
      return button.phoneNumber && button.phoneNumber.trim() !== '';
    }
    if (button.type === 'cta_copy') {
      return button.copyCode && button.copyCode.trim() !== '';
    }
    if (button.type === 'quick_reply') {
      return button.id && button.id.trim() !== '';
    }
    if (button.type === 'voice_call') {
      return true; // Only requires text, no additional fields
    }
    return true;
  });
}, {
  message: "URL is required for URL buttons, phone number for call buttons, copy code for copy buttons, and ID for quick reply buttons",
  path: ["buttons"],
});

interface ImprovedButtonMessageFormProps {
  onFormChange: (data: ButtonFormData) => void;
  onGenerate: (message: string) => void;
  initialValues?: ButtonFormData;
}

export function ImprovedButtonMessageForm({ onFormChange, onGenerate, initialValues }: ImprovedButtonMessageFormProps) {
  const { t } = useTranslation();
  
  const buttonTypeOptions = [
    { value: 'quick_reply', label: t('buttonMessage.form.quickReply'), icon: MessageSquare },
    { value: 'cta_copy', label: t('buttonMessage.form.copyCode'), icon: Copy },
    { value: 'cta_call', label: t('buttonMessage.form.call'), icon: Phone },
    { value: 'cta_url', label: t('buttonMessage.form.visitWebsite'), icon: ExternalLink },
    { value: 'voice_call', label: t('buttonMessage.form.voiceCall'), icon: PhoneCall },
  ];
  const onFormChangeRef = useRef(onFormChange);
  onFormChangeRef.current = onFormChange;

  const form = useForm<ButtonFormData>({
    resolver: zodResolver(buttonFormSchema),
    defaultValues: initialValues ?? {
      title: '',
      subTitle: '',
      mediaType: 'none',
      mediaUrl: '',
      mediaFilename: '',
      buttons: [
        {
          type: 'quick_reply',
          text: '',
          id: '',
          url: '',
          phoneNumber: '',
          copyCode: '',
        },
      ],
    },
  });
  const isResettingRef = React.useRef(false);

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'buttons',
  });

  const mediaType = form.watch('mediaType');

  // Update parent component when form changes
  React.useEffect(() => {
    const subscription = form.watch((value) => {
      if (isResettingRef.current) return;
      onFormChangeRef.current(value as ButtonFormData);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Reset when initialValues change
  React.useEffect(() => {
    if (!initialValues) return;
    const current = form.getValues();
    const same = JSON.stringify(current) === JSON.stringify(initialValues);
    if (!same) {
      isResettingRef.current = true;
      form.reset(initialValues);
      queueMicrotask(() => {
        isResettingRef.current = false;
      });
    }
  }, [initialValues, form]);

  const onSubmit = (data: ButtonFormData) => {
    // Dynamic import to avoid require
    import('@/utils/messageGenerator').then(({ generateButtonMessage }) => {
      const message = generateButtonMessage(data);
      onGenerate(message);
    });
  };

  const addButton = () => {
    append({
      type: 'quick_reply',
      text: '',
      id: '',
      url: '',
      phoneNumber: '',
      copyCode: '',
    });
  };

  const getButtonIcon = (type: string) => {
    const option = buttonTypeOptions.find(opt => opt.value === type);
    return option ? option.icon : MessageSquare;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('buttonMessage.title')}</CardTitle>
        <CardDescription>
          {t('buttonMessage.description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Fields */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
              <FormItem>
                <FormLabel>{t('buttonMessage.form.title')} <span className="text-red-500">*</span></FormLabel>
                <FormControl>
                  <TextareaWithPicker 
                    placeholder={t('placeholders.title')}
                    className="min-h-[80px]"
                    {...field}
                    onChange={(e) => field.onChange(stripButtonDisallowed(e.target.value))}
                  />
                </FormControl>
                <FormDescription>
                  {t('buttonMessage.form.subTitleDescription')}
                </FormDescription>
                <FormMessage />
              </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="subTitle"
              render={({ field }) => (
              <FormItem>
                <FormLabel>{t('buttonMessage.form.subTitle')} <span className="text-gray-400">{t('common.optional')}</span></FormLabel>
                <FormControl>
                  <TextareaWithPicker
                    placeholder={t('placeholders.footer')}
                    className="min-h-[80px]"
                    {...field}
                    onChange={(e) => field.onChange(stripButtonDisallowed(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
              )}
            />

            <Separator />

            {/* Media Section */}
            <div>
              <h3 className="text-lg font-medium mb-4">{t('buttonMessage.form.mediaAttachment')}</h3>
              
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="mediaType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('buttonMessage.form.mediaType')}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('placeholders.selectMediaType')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">{t('buttonMessage.form.noMedia')}</SelectItem>
                          <SelectItem value="image">
                            <div className="flex items-center">
                              <ImageIcon className="w-4 h-4 mr-2" />
                              {t('buttonMessage.form.image')}
                            </div>
                          </SelectItem>
                          <SelectItem value="video">
                            <div className="flex items-center">
                              <Video className="w-4 h-4 mr-2" />
                              {t('buttonMessage.form.video')}
                            </div>
                          </SelectItem>
                          <SelectItem value="document">
                            <div className="flex items-center">
                              <FileText className="w-4 h-4 mr-2" />
                              {t('buttonMessage.form.document')}
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {mediaType !== 'none' && (
                  <FormField
                    control={form.control}
                    name="mediaUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('buttonMessage.form.mediaUrl')}</FormLabel>
                        <FormControl>
                          <InputWithPicker 
                            placeholder={
                              mediaType === 'document' 
                                ? t('placeholders.mediaUrlDocument')
                                : t('placeholders.mediaUrl')
                            }
                            {...field}
                            onChange={(e) => field.onChange(stripButtonDisallowed(e.target.value))}
                          />
                        </FormControl>
                        <FormDescription>
                          {t('buttonMessage.form.mediaUrlDescription', { type: t(`buttonMessage.form.${mediaType}`) })}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {mediaType === 'document' && (
                  <FormField
                    control={form.control}
                    name="mediaFilename"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('buttonMessage.form.mediaFilename')}</FormLabel>
                        <FormControl>
                          <InputWithPicker 
                            placeholder={t('placeholders.mediaFilename')}
                            {...field}
                            onChange={(e) => field.onChange(stripButtonDisallowed(e.target.value))}
                          />
                        </FormControl>
                        <FormDescription>
                          {t('buttonMessage.form.mediaFilenameDescription')}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>

            <Separator />

            {/* Buttons Section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-medium">{t('buttonMessage.form.buttons')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('buttonMessage.form.buttonsDescription')}
                  </p>
                </div>
                <div className="text-sm text-muted-foreground">
                  {fields.length} / 10 {t('buttonMessage.form.buttons')}
                </div>
              </div>

              <div className="space-y-4">
                {fields.map((field, index) => {
                  const buttonType = form.watch(`buttons.${index}.type`);
                  const ButtonIcon = getButtonIcon(buttonType);
                  
                  return (
                    <Card key={field.id} className="p-4 border-2 border-dashed border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                          <ButtonIcon className="w-4 h-4 mr-2" />
                          <h4 className="text-sm font-medium">{t('buttonMessage.form.buttonType')} #{index + 1}</h4>
                        </div>
                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => remove(index)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      
                      {/* First row: Button Type and Button Text */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <FormField
                          control={form.control}
                          name={`buttons.${index}.type`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('buttonMessage.form.buttonType')}</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder={t('placeholders.selectButtonType')} />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {buttonTypeOptions.map((option) => {
                                    const Icon = option.icon;
                                    return (
                                      <SelectItem key={option.value} value={option.value}>
                                        <div className="flex items-center">
                                          <Icon className="w-4 h-4 mr-2" />
                                          {option.label}
                                        </div>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={form.control}
                          name={`buttons.${index}.text`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{t('buttonMessage.form.buttonText')} <span className="text-red-500">*</span></FormLabel>
                              <FormControl>
                                <InputWithPicker placeholder={t('placeholders.buttonText')} {...field} onChange={(e) => field.onChange(stripButtonDisallowed(e.target.value))} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Second row: Conditional field based on button type */}
                      <div className="w-full">
                        {buttonType === 'quick_reply' && (
                          <FormField
                            control={form.control}
                            name={`buttons.${index}.id`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('buttonMessage.form.buttonId')} <span className="text-red-500">*</span></FormLabel>
                                <FormControl>
                                  <Input placeholder={t('placeholders.buttonId')} {...field} onChange={(e) => field.onChange(stripButtonDisallowed(e.target.value))} />
                                </FormControl>
                                <FormDescription>
                                  {t('buttonMessage.form.buttonIdDescription')}
                                </FormDescription>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}

                        {buttonType === 'cta_url' && (
                          <FormField
                            control={form.control}
                            name={`buttons.${index}.url`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('buttonMessage.form.url')} <span className="text-red-500">*</span></FormLabel>
                                <FormControl>
                                  <InputWithPicker placeholder={t('placeholders.url')} {...field} onChange={(e) => field.onChange(stripButtonDisallowed(e.target.value))} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}

                        {buttonType === 'cta_call' && (
                          <FormField
                            control={form.control}
                            name={`buttons.${index}.phoneNumber`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('buttonMessage.form.phoneNumber')} <span className="text-red-500">*</span></FormLabel>
                                <FormControl>
                                  <InputWithPicker placeholder={t('placeholders.phoneNumber')} {...field} onChange={(e) => field.onChange(stripButtonDisallowed(e.target.value))} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}

                        {buttonType === 'cta_copy' && (
                          <FormField
                            control={form.control}
                            name={`buttons.${index}.copyCode`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>{t('buttonMessage.form.textCodeToCopy')} <span className="text-red-500">*</span></FormLabel>
                                <FormControl>
                                  <Input placeholder={t('placeholders.textCodeToCopy')} {...field} onChange={(e) => field.onChange(stripButtonDisallowed(e.target.value))} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        )}

                        {buttonType === 'voice_call' && (
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <p className="text-sm text-blue-700">
                              {t('buttonMessage.form.voiceCallHelper')}
                            </p>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>

              {/* Add Button - Now at the bottom */}
              <div className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addButton}
                  className="w-full"
                  disabled={fields.length >= 10}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('buttonMessage.form.addButton')} ({fields.length}/10)
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}